import type { ResponseCodes } from './utils.type';

const ExcludeNonFocusableElements =
    ':not([disabled]):not(.-hidden):not([type="hidden"]):not([tabindex="-1"])';
/**
 * All possible HTML5 boolean attributes.
 * @memberof UI
 * @alias BooleanAttributes
 * @type {Array<string>}
 */
export const BooleanAttributes = [
    'allowfullscreen',
    'async',
    'autofocus',
    'autoplay',
    'checked',
    'controls',
    'default',
    'defer',
    'disabled',
    'formnovalidate',
    'indeterminate',
    'inert',
    'ismap',
    'itemscope',
    'loop',
    'multiple',
    'muted',
    'nomodule',
    'novalidate',
    'open',
    'playsinline',
    'readonly',
    'required',
    'reversed',
    'selected',
    'shadowrootclonable',
    'shadowrootdelegatesfocus',
    'shadowrootserializable',
];

/**
 * List of primary interactive elements. Usually used to determine
 * focusable elements.
 * @memberof UI
 * @alias PrimaryInteractiveElements
 * @type {Array<string>}
 */
export const PrimaryInteractiveElements = [
    'input',
    'select',
    'textarea',
    'summary',
    '[tabindex]',
].map((selector) => `${selector}${ExcludeNonFocusableElements}`);

/**
 * List of secondary interactive elements. Usually used to determine
 * focusable elements.
 * @memberof UI
 * @alias SecondaryInteractiveElements
 * @type {Array<string>}
 */
export const SecondaryInteractiveElements = ['a[href]', 'button'].map(
    (selector) => `${selector}${ExcludeNonFocusableElements}`
);

/**
 * SVG - related tags
 * @memberof UI
 * @alias SVGElements
 * @type {Array<string>}
 */
export const SVGElements = [
    'svg',
    'use',
    'path',
    'g',
    'defs',
    'filter',
    'feComponentTransfer',
    'feFuncR',
    'feFuncG',
    'feFuncB',
];

/**
 * Input elements, including fieldset-friendly elements (radios and checkboxes).
 * @memberof UI
 * @alias BaseNativeFormElements
 * @type {Array}
 */
export const BaseNativeFormElements = ['input', 'select', 'textarea', 'output'];

/**
 * Input elements, excluding fieldset-friendly elements (radios and checkboxes).
 * @memberof UI
 * @alias NativeFormElements
 * @type {Array}
 */
export const NativeFormElements = [
    'input:not([type=radio]):not([type=checkbox])',
    'input[type="checkbox"]:only-of-type',
    'select',
    'textarea',
    'output',
    'summary',
];

export const CustomVirtualFormElements = [
    'ui-dropdown',
    'ui-slider',
    'ui-meterbar',
    'ui-monthpicker',
    'ui-monthrange',
    'ui-timerange',
    'ui-stepper',
    'ui-rating',
];

/**
 * Custom input elements.
 * @memberof UI
 * @alias CustomFormElements
 * @type {Array}
 */
export const CustomFormElements = [
    'ui-autocomplete',
    'ui-textcounter',
    'ui-searchinput',
    'ui-daterange',
    'ui-datepicker',
    'ui-upload',
    'ui-timepicker',
    ...CustomVirtualFormElements,
];

/**
 * @type {PopoverAlignment}
 */
export const PopoverAlignment = {
    Top: 'top',
    Middle: 'middle',
    Bottom: 'bottom',
    Left: 'left',
    Right: 'right',
    None: 'none',
};

/**
 * @typedef {("top" | "middle" | "bottom" | "left" | "right" | "none")} AlignmentValue
 */
export type AlignmentValue =
    | 'top'
    | 'middle'
    | 'bottom'
    | 'left'
    | 'right'
    | 'none';

/**
 * @type {Pick<PopoverAlignment, "Left" | "Right" | "None">}
 */
export const PopoverShifting = {
    Left: PopoverAlignment.Left,
    Right: PopoverAlignment.Right,
    None: PopoverAlignment.None,
};

/**
 * @type {string}
 */
export const MenuOverlayCSSClass = '-ui-menu-overlay';

/**
 * @type {string}
 */
export const TopModalCSSClass = '-ui-top-modal';

export const TimeInSeconds: Record<string, number> = {
    Second: 1,
    Minute: 60,
    Hour: 3600,
    Day: 86400,
    Week: 604800,
} as const;

export enum Mapped {
    True,
    False,
    Image,
    Alt,
}

export const ResponseStatusCode: ResponseCodes = {
    BadRequest: 400,
    NotFound: 404,
    ServerError: 500,
    Success: 200,
} as const;
